import { apiRequest, type components } from './core';

/**
 * School Config API endpoints
 */
export type SchoolConfigResponse = components['schemas']['SchoolConfigResponse'];
export type UpdateSchoolConfigData = components['schemas']['UpdateSchoolConfigRequest'];

export const schoolConfigApi = {
  /**
   * Get school configuration
   * Returns tenant-specific settings including branding, terminology, and behavior rules
   */
  async get() {
    const response = await apiRequest<{ success: boolean; data: SchoolConfigResponse }>('/api/school-config');
    return response.data;
  },

  /**
   * Get SMS digest preview for all 3 detail levels (admin only)
   * Accepts optional counts to customize sample data for the preview.
   */
  async getSmsDigestPreview(params?: {
    negativeBehaviors?: number;
    positiveBehaviors?: number;
    attendanceIssues?: number;
  }) {
    const query = new URLSearchParams();
    if (params?.negativeBehaviors !== undefined) query.set('negativeBehaviors', String(params.negativeBehaviors));
    if (params?.positiveBehaviors !== undefined) query.set('positiveBehaviors', String(params.positiveBehaviors));
    if (params?.attendanceIssues !== undefined) query.set('attendanceIssues', String(params.attendanceIssues));
    const qs = query.toString();
    const response = await apiRequest<{
      success: boolean;
      data: { summary: string; categories: string; detailed: string };
    }>(`/api/school-config/sms-digest-preview${qs ? `?${qs}` : ''}`);
    return response.data;
  },

  /**
   * Update school configuration (admin only)
   * Updates behavior settings, branding, terminology, and other school config
   */
  async update(data: UpdateSchoolConfigData) {
    return apiRequest<{ success: boolean; data: SchoolConfigResponse; message: string }>('/api/school-config', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },
};

/**
 * School Configuration - Parent Portal API
 */
export const schoolConfigParentPortalApi = {
  /**
   * Get parent portal configuration
   */
  getConfig: async () => {
    return apiRequest<{
      success: boolean;
      data: { linkCodeExpirationDays: number };
    }>('/api/school-config/parent-portal', {
      method: 'GET',
    });
  },

  /**
   * Update parent portal configuration
   */
  updateConfig: async (linkCodeExpirationDays: number) => {
    return apiRequest<{
      success: boolean;
      message: string;
    }>('/api/school-config/parent-portal', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ linkCodeExpirationDays }),
    });
  },
};
